import {CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList, moveItemInArray} from '@angular/cdk/drag-drop';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, inject } from '@angular/core';
import {FormArray, FormBuilder, FormGroup, ReactiveFormsModule} from '@angular/forms';
import { NOTIFICATIONS, NotificationsService } from 'src/app/core/service/notifications.service';
import { ToolTipSettings } from 'src/app/domain/shared/component/tooltip/tooltip.util';
import {
  SUGGESTED_GROUPS,
  SUGGESTED_ROUNDS_DATA,
  SUGGESTED_ROUNDS_NAMES,
  SUGGESTED_SUB_GROUPS,
} from '../pojo/fund-strategy-constants';
import { EditStatus, TotalControlNames } from '../pojo/fund-strategy-enums';
import {
  FsSettingsData,
  FundStrategyExportExcelData,
  GroupFS,
  RoundsFS,
  SaveModelEmitterDTO,
  SubGroupFS, SubGroupTooltipAnalytics,
  TotalsFS,
} from '../pojo/fund-strategy-export-classes';
import { FsGroupFormGroup, FsSubGroupTotalsValue, SubGroupFormGroup } from '../pojo/fund-strategy-export-types';
import { FundStrategyFormService } from '../service/fund-strategy-form-group';
import { FundStrategyService } from '../service/fund-strategy-service';
import { FsScrollPositionService } from '../service/fs-scroll-position.service';
import { FsChartsCalcService } from '../fund-strategy-charts/fs-charts-service/fs-charts-calc.service';
import { ExcelService } from 'src/app/domain/excel';
import { ExcelTemplateName } from 'src/app/domain/excel/interfaces';
import { Subject, take, timer } from "rxjs";
import { takeUntil } from "rxjs/operators";
import {
  UnsavedChangesModalService
} from "../../../shared/component/unsaved-changes-modal-wrapper/service/unsaved-changes-modal.service";
import { ButtonComponent } from "../../../shared/component/buttons/button.component";
import { MatMenu, MatMenuTrigger } from "@angular/material/menu";
import { DropDownItemComponent } from "../../../shared/component/drop-down-item/drop-down-item.component";
import { ThreeDotsMenuComponent } from "../../../shared/component/three-dots-menu/three-dots-menu.component";
import { InputComponent } from "../../../shared/component/inputs/input-component/input.component";
import { DecimalPipe, NgClass, PercentPipe } from "@angular/common";
import { TableGroupIconComponent } from "../../../shared/component/table-group-icon/table-group-icon.component";
import { AllIconsComponent } from "../../../shared/component/all-icons/all-icons.component";
import { SwitcherDashBracesPipe } from "../../../../util/pipes/switcherDashBraces.pipe";
import {
  InputAutocompleteComponent
} from "../../../shared/component/inputs/input-autocomplete/input-autocomplete.component";
import { FpModifyValuePipe } from "../../../../util/pipes/fp-modify-value.pipe";
import { PluralizePipe } from "../../../../util/pipes/pluralize.pipe";
import { TooltipComponent } from "../../../shared/component/tooltip/tooltip.component";
import {VarDirective} from "../../../../util/directives/var-directive";


@Component({
  selector: 'app-fund-strategy-table',
  templateUrl: './fund-strategy-table.component.html',
  styleUrls: ['./fund-strategy-table.component.scss'],
  imports: [
    ReactiveFormsModule,
    ButtonComponent,
    MatMenu,
    DropDownItemComponent,
    MatMenuTrigger,
    ThreeDotsMenuComponent,
    InputComponent,
    TableGroupIconComponent,
    CdkDrag,
    AllIconsComponent,
    SwitcherDashBracesPipe,
    DecimalPipe,
    NgClass,
    CdkDropList,
    CdkDragHandle,
    InputAutocompleteComponent,
    PercentPipe,
    FpModifyValuePipe,
    PluralizePipe,
    TooltipComponent,
    VarDirective
]
})


export class FundStrategyTableComponent implements OnChanges, OnDestroy {
  private fb = inject(FormBuilder);
  private notificationsService = inject(NotificationsService);
  private formCreationService = inject(FundStrategyFormService);
  private unsavedChangesModalService = inject(UnsavedChangesModalService);
  private fundStrategyService = inject(FundStrategyService);
  private excelService = inject(ExcelService);
  private fsScrollPositionService = inject(FsScrollPositionService);

  private _destroyStream: Subject<void> = new Subject<void>();

  @Input() groups: GroupFS[];
  @Input() modelName: string;
  @Input() saveModel = false;
  @Input() isModelBlocked = false;
  // uses for export excel only
  @Input() metricsData = new FsSettingsData();
  @Output() updateModelEmitter: EventEmitter<SaveModelEmitterDTO> = new EventEmitter<SaveModelEmitterDTO>();
  @Output() exportExcelEmitter = new EventEmitter<FundStrategyExportExcelData>();
  @Output() showRoundsEmitter: EventEmitter<boolean> = new EventEmitter<boolean>();

  form: FormGroup<{
    fundStrategy: FormArray<FsGroupFormGroup>;
  }>;

  model = {
    editStatus: EditStatus,
    showMainTable: true, // needed to hide table with groups/subgroups when you go in rounds
    group: {
      editName: new Map<string, boolean>(),
      isEdited: false, // needed to show, that groupName edit process is started
    },
    subGroup: {
      editName: new Map<string, boolean>(),
      visibility: Array<{ visible: boolean }>(), // to show/hide rows in table
      isEdited: false, // needed to show, that subGroupName edit process is started
      originalState: [] as SubGroupFS[],
      totalsOriginalState: null as FsSubGroupTotalsValue,
      lastEditedGroupSubGroupIndex: '', // last edited group-subgroup index, ex. '1-2'
    },
    rounds: {
      show: new Map<string, boolean>(),
      areInEditMode: false, // needed to show, that rounds edit process is started
      editMode: new Map<string, boolean>(),
      suggestedNames: SUGGESTED_ROUNDS_NAMES,
      originalState: [] as RoundsFS[],
      previous: {
        holding: null as number, // needed for holding calculations. E.x. Holding in round 2 is depends on holding in round 1
        compNumber: null as number, // needed for companies (number) calculations.
        // E.x. You have 10 companies in round 1. ExitCompanies = 2, WriteOff companies = 3
        // In the round 2, only 5 companies should be displayed
      },
      input: {
        error: new Map<string, boolean>(),
        exitCompPriceError: new Map<string, boolean>(),
      },
      unallocatedCompanies: new Map<string, boolean>(),
      isAllCompaniesAllocated: false,
    },
    totals: {
      subTotal: [] as TotalsFS[], // total values for 1 Group
      grand: {} as TotalsFS, // total values for all Groups
      names: TotalControlNames,
    },
  };

  toolTipSettings: ToolTipSettings & { xShift: string } = {
    show: false,
    title: '',
    xShift: '',
  };

  subGroupTooltipSettings = {
    show: false,
    data: [] as SubGroupTooltipAnalytics[],
    coordinates: undefined as DOMRect | undefined,
  };

  isNotificationShown = false;

  ngOnDestroy(): void {
    // Clear saved scroll position if user navigates away without using Back button
    this.fsScrollPositionService.clearScrollPosition();
    this._destroyStream.next();
    this._destroyStream.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.isModelBlocked && changes.isModelBlocked) {
      this.prepareModelBlocked();
    }

    if (changes.groups && this.groups) {
      this.prepareFormAndVisibility();
      this.prepareDataForExportExcel();

      // this.model.rounds.areInEditMode = false will be set after model update
      // BUG FIX: when user click save rounds -> redirected to main table, due to
      // model will be updated and in ngOnChange will be called resetShowRoundsTableData()
      // which leads to redirect
      // EXPECTED BEHAVIOUR: after save rounds -> stay in rounds table
      if (this.model.rounds.areInEditMode) {
        this.model.rounds.areInEditMode = false;
      } else {
        // Is needed when user is in rounds table and click 'Change model'
        // BUG FIX: model broken or open round table in another model (if index exist)
        // EXPECTED BEHAVIOUR: reset all data and redirect to main table
        this.resetShowRoundsTableData();
      }
    }

    this.updateEditStatusAfterOrgChange();
  }

  /**
   * Updates @param subGroup after changes in:
   * holding, companies number, writeOff, or exitCompNumber.
   * Calculates new totals.
   *
   * @param subGroup with new values.
   * @param grIndex is the group index.
   */
  updateSubGroup(subGroup: SubGroupFormGroup, grIndex: number, sbIndex: number): void {
    const updatedSubGroup = this.formCreationService.fillSubGroupFs(subGroup.value as SubGroupFS);
    subGroup.patchValue(updatedSubGroup.value);
    this.model.rounds.input = this.formCreationService.checkWriteOffAndExitValue(subGroup, grIndex, sbIndex);
    this.formCreationService.checkExitCompPriceValue(subGroup, grIndex, sbIndex);
    const unallocatedCompaniesInfo = this.formCreationService.changeStyleForUnallocatedCompanies(subGroup, grIndex, sbIndex);
    this.model.rounds.unallocatedCompanies = unallocatedCompaniesInfo.unallocatedCompanies;
    this.model.rounds.isAllCompaniesAllocated = unallocatedCompaniesInfo.isAllCompaniesAllocated;
    this.model.totals.subTotal[grIndex] = this.fundStrategyService.calculateSubTotals(grIndex, this.form.controls.fundStrategy);
    this.model.totals.grand = this.fundStrategyService.calculateGrandTotal(this.model.totals.subTotal);
  }

  // ADD NEW OBJECT //////////////////////////////////

  addNewGroup(): void {
    this.showNotificationForNewModel();
    const fundStrategyArray = this.form.controls.fundStrategy as FormArray;
    fundStrategyArray.push(this.formCreationService.fillGroupFs({
        name: '',
        grOrder: fundStrategyArray.length,
        subGroups: SUGGESTED_SUB_GROUPS,
      },
    ));
    this.unsavedChangesModalService.setIsInEditMode(true);
    this.model.group.isEdited = true; // needed to cancel the group creation
    this.editGroupOrSubGroup((fundStrategyArray.length - 1).toString(), this.model.editStatus.edit, false);
    this.recalculateTotals();
  }

  addNewSubGroup(grIndex: number): void {
    this.showNotificationForNewModel();
    const fundStrategyArray = this.form.controls.fundStrategy;
    const subGroupsArray = fundStrategyArray.controls[grIndex].controls.subGroups;
    subGroupsArray.push(this.formCreationService.fillSubGroupFs({
      name: '',
      subGrOrder: subGroupsArray.length,
      roundsData: SUGGESTED_ROUNDS_DATA,
    }));
    const groupSubgroupIndex = (grIndex + '-' + (subGroupsArray.length - 1)).toString();
    this.unsavedChangesModalService.setIsInEditMode(true);
    this.model.subGroup.isEdited = true; // needed to cancel the sub group creation
    this.editGroupOrSubGroup(groupSubgroupIndex, this.model.editStatus.edit, true);
    this.checkSubGroupVisibility(grIndex);
    this.recalculateTotals();
  }

  addNewRound(grIndex: number, sbIndex: number): void {
    const fundStrategyArray = this.form.controls.fundStrategy;
    const subGroupsArray = fundStrategyArray.controls[grIndex].controls.subGroups;
    const subGroupFormGroup = subGroupsArray.at(sbIndex);
    const newRoundsData = new RoundsFS();
    const roundsArray = subGroupFormGroup.get('roundsData') as FormArray;
    // update all rounds order
    roundsArray.controls.forEach((roundFormGroup, index) => {
      roundFormGroup.get('roundOrder').patchValue(index);
    });
    newRoundsData.roundOrder = roundsArray.length;

    // Copy esop from previous round if it exists - Artem requirement
    if (roundsArray.length > 0) {
      const previousRound = roundsArray.controls[roundsArray.length - 1].getRawValue();
      newRoundsData.esop = previousRound.esop;
    }

    roundsArray.push(this.formCreationService.fillRoundsFs(newRoundsData));
    this.updateSubGroup(subGroupFormGroup, grIndex, sbIndex);
    this.recalculateTotals();
  }

  // DELETE OBJECT //////////////////////////////////

  deleteGroup(grIndex: number): void {
    const fundStrategyArray = this.form.controls.fundStrategy as FormArray;
    fundStrategyArray.removeAt(grIndex);
    this.updateModelHandler(EditStatus.deleteGroup, grIndex);
    this.recalculateTotals();
  }

  /**
   * Exports the rounds data of a specific subgroup within a fund strategy to an Excel file.
   *
   * @param {number} index - The index of the subgroup within the fund strategy form control.
   */
  exportRoundsTable(index: number, sbIndex: number): void {
    // TODO CHECK WITH ARS WHEN IT WILL BE DONE
    const rounds = this.form.controls.fundStrategy.controls[index].controls.subGroups.controls[sbIndex].controls.roundsData.getRawValue();;
    this.excelService.exportByTemplate(rounds, 'fund-strategy-rounds.xlsx', ExcelTemplateName.fundStrategyInternal);
  }

  /**
   * Exports the fund strategy group data to an Excel file.
   */
  exportGroupTable(): void {
    const fileName = 'fund-strategy-group.xlsx';
    const gr = this.form.controls.fundStrategy.controls;
    const dataForExport: FundStrategyExportExcelData = {
      groups: gr,
      metrics: this.metricsData,
    };
    this.excelService.exportByTemplate(dataForExport, fileName, ExcelTemplateName.fundStrategyExternal);
  }

  deleteSubGroup(grIndex: number, sbIndex: number): void {
    const fundStrategyArray = this.form.controls.fundStrategy;
    const subGroupsArray = fundStrategyArray.controls[grIndex].controls.subGroups;
    subGroupsArray.removeAt(sbIndex);
    this.updateModelHandler(EditStatus.deleteSubGroup, grIndex, sbIndex);
    this.recalculateTotals();
  }

  deleteRound(grIndex: number, sbIndex: number, roundIndex: number): void {
    const fundStrategyArray = this.form.controls.fundStrategy;
    const subGroupsArray = fundStrategyArray.controls[grIndex].controls.subGroups;
    const roundsArray = subGroupsArray.controls[sbIndex].controls.roundsData;

    roundsArray.removeAt(roundIndex);
    // update all rounds order
    roundsArray.controls.forEach((roundFormGroup, index) => {
      roundFormGroup.get('roundOrder').patchValue(index);
    });
    this.model.rounds.previous.holding = null;
    this.updateSubGroup(subGroupsArray.at(sbIndex) as FormGroup, grIndex, sbIndex);
    this.recalculateTotals();
  }

  // DUPLICATE OBJECT //////////////////////////////////

  duplicateGroup(grIndex: number): void {
    const fundStrategyArray = this.form.controls.fundStrategy as FormArray;
    const groupToDuplicate = fundStrategyArray.controls[grIndex];
    const duplicatedGroup = this.formCreationService.fillGroupFs(groupToDuplicate.value);
    duplicatedGroup.patchValue({ uuid: null, name: duplicatedGroup.value.name + ' (copy)', grOrder: grIndex + 1 });
    fundStrategyArray.insert(grIndex + 1, duplicatedGroup);
    this.updateModelHandler(EditStatus.duplicateGroup, grIndex);
    this.checkSubGroupVisibility(grIndex);
    this.recalculateTotals();
    this.editGroupOrSubGroup((grIndex + 1).toString(), EditStatus.edit, false);
  }

  duplicateSubGroup(grIndex: number, sbIndex: number): void {
    const fundStrategyArray = this.form.controls.fundStrategy;
    const subGroupsArray = fundStrategyArray.controls[grIndex].controls.subGroups;
    const subGroupToDuplicate = subGroupsArray.controls[sbIndex].value;

    const duplicatedSubGroup = this.formCreationService.fillSubGroupFs(subGroupToDuplicate as SubGroupFS);
    duplicatedSubGroup.patchValue({ uuid: null, subGrOrder: sbIndex + 1, name: subGroupToDuplicate.name + ' (copy)' });

    subGroupsArray.insert(sbIndex + 1, duplicatedSubGroup);
    this.updateModelHandler(EditStatus.duplicateSubGroup, grIndex, sbIndex);
    this.recalculateTotals();
    this.editGroupOrSubGroup((grIndex + '-' + (sbIndex + 1)).toString(), EditStatus.edit, true);
  }

  duplicateRound(grIndex: number, sbIndex: number, roundIndex: number): void {
    const fundStrategyArray = this.form.controls.fundStrategy;
    const subGroupsArray = fundStrategyArray.controls[grIndex].controls.subGroups;
    const roundsArray = subGroupsArray.controls[sbIndex].controls.roundsData;
    const roundToDuplicate = roundsArray.controls[roundIndex];
    const duplicatedRound = this.fb.group(roundToDuplicate.getRawValue());

    // after adding the new round, order in all rounds should be changed
    for (let i = 0; i < roundsArray.controls.length; i++) {
      if (i > roundIndex) {
        roundsArray.at(i).patchValue({ roundOrder: i + 1 });
      }
    }
    // add new order number to the duplicatedRound
    duplicatedRound.patchValue({ uuid: null, roundOrder: roundIndex + 1 });
    roundsArray.insert(roundIndex + 1, duplicatedRound);
    this.updateSubGroup(subGroupsArray.at(sbIndex) as FormGroup, grIndex, sbIndex);
    this.recalculateTotals();
  }

  // EDIT OBJECT //////////////////////////////////

  editGroupOrSubGroup(indexes: string, status: EditStatus, isSubGroup: boolean): void {
    const grIndex = Number(indexes.split('-')[0]);
    const sbIndex = Number(indexes.split('-')[1]);
    const fundStrategyArray = this.form.controls.fundStrategy as FormArray;
    const targetArray = isSubGroup ? (fundStrategyArray.at(grIndex) as FormGroup).controls.subGroups as FormArray : fundStrategyArray;
    const modelTarget = isSubGroup ? this.model.subGroup : this.model.group;

    switch (status) {
      case EditStatus.edit:
        this.model.subGroup.originalState = targetArray.getRawValue();
        modelTarget.editName.set(indexes.toString(), true);
        break;
      case EditStatus.decline:
        if (modelTarget.isEdited) {
          targetArray.removeAt(isSubGroup ? sbIndex : grIndex);
          modelTarget.isEdited = false;
        } else {
          targetArray.reset(this.model.subGroup.originalState);
          modelTarget.editName.set(indexes.toString(), false);
          modelTarget.isEdited = false;
        }
        this.unsavedChangesModalService.setIsInEditMode(false);
        break;
      case EditStatus.confirm:
        if (!this.form.value.fundStrategy[grIndex].name) {
          this.notificationsService.warning(NOTIFICATIONS.EMPTY_GROUP_NAME_WARNING);
          return;
        }
        if (isSubGroup && !this.form.value.fundStrategy[grIndex].subGroups[sbIndex].name) {
          this.notificationsService.warning(NOTIFICATIONS.EMPTY_SUBGROUP_NAME_WARNING);
          return;
        }
        // eslint-disable-next-line no-nested-ternary
        const editStatus = modelTarget.isEdited
          ? (isSubGroup ? EditStatus.addSubGroup : EditStatus.addGroup)
          : (isSubGroup ? EditStatus.updateSubGroupName : EditStatus.updateGroupName);

        this.updateModelHandler(editStatus, grIndex, sbIndex);
        modelTarget.editName.set(indexes.toString(), false);
        modelTarget.isEdited = false;
        this.unsavedChangesModalService.setIsInEditMode(false);
        break;
    }
  }

  updateEditStatusAfterOrgChange(): void {
    this.unsavedChangesModalService.getDecisionResult().pipe(takeUntil(this._destroyStream)).subscribe((decision) => {
      if (decision) {
        this.editAllRounds(this.model.subGroup.lastEditedGroupSubGroupIndex, EditStatus.decline);
      }
    });
  }

  editAllRounds(groupSubgroupIndex: string, status: EditStatus): void {
    const parts = groupSubgroupIndex.split('-'); // divide the string by '-'
    const grIndex = Number(parts[0]);
    const sbIndex = Number(parts[1]);

    const fundStrategyArray = this.form.controls.fundStrategy;
    const subGroupsArray = fundStrategyArray.controls[grIndex].controls.subGroups;
    const subGroup = subGroupsArray.at(sbIndex);


    if (status === EditStatus.edit) {
      this.model.rounds.editMode.set(groupSubgroupIndex, true);
      this.model.subGroup.lastEditedGroupSubGroupIndex = groupSubgroupIndex;
      this.unsavedChangesModalService.setIsInEditMode(true);
      this.model.rounds.originalState = subGroup.controls.roundsData.getRawValue();
      this.model.subGroup.totalsOriginalState = subGroup.controls.total.getRawValue();
      this.model.rounds.areInEditMode = true;
    }
    if (status === EditStatus.decline) {
      this.unsavedChangesModalService.setIsInEditMode(false);
      this.model.rounds.editMode.set(groupSubgroupIndex, false);
      subGroup.patchValue({ roundsData: this.model.rounds.originalState });
      subGroup.controls.total.patchValue(this.model.subGroup.totalsOriginalState);
      this.model.rounds.areInEditMode = false;
      this.model.rounds.input.error.set(groupSubgroupIndex + '-0', false);
    }
    if (status === EditStatus.confirm) {
      this.unsavedChangesModalService.setIsInEditMode(false);
      this.model.rounds.editMode.set(groupSubgroupIndex, false);
      this.updateModelHandler(EditStatus.updateSubGroup, grIndex, sbIndex);
      // this.model.rounds.areInEditMode = false will be set in ngOnChange after model will be updated
      // BUG FIX: when user click save rounds -> redirected to main table, due to
      // model will be updated and in ngOnChange will be called resetShowRoundsTableData()
      // which leads to redirect
      // EXPECTED BEHAVIOUR: after save rounds -> stay in rounds table
    }
  }

  // ADDITIONAL FUNCTIONS //////////////////////////////////

  /**
   * Show notification, that model is blocked and user should save model with name.
   * To prevent multiple notifications after multiple clicks, flag notificationShown will be reset after 3 seconds
   *
   * In buttons additional (click) emitter is used, due to disabled button property
   */
  showNotificationForNewModel(): void {
    if (this.isModelBlocked && !this.isNotificationShown) {
      this.notificationsService.warning(NOTIFICATIONS.SAVE_MODEL_FIRST_WARNING);
      this.isNotificationShown = true;

      timer(3000)
        .pipe(take(1))
        .subscribe(() => {
          this.isNotificationShown = false;
        });
    }
  }

  /**
   * Shows the round table and hide the main table.
   *
   * @param groupSubgroupIndex has a pattern of `grIndex + '-' + sbIndex`.
   */
  showRounds(subGroup: SubGroupFormGroup, grIndex: number, sbIndex: number): void {
    this.fsScrollPositionService.saveScrollPosition();
    this.model.showMainTable = false;
    this.model.rounds.show.set(grIndex + '-' + sbIndex, true);
    const unallocatedCompaniesInfo = this.formCreationService.changeStyleForUnallocatedCompanies(subGroup, grIndex, sbIndex);
    this.model.rounds.unallocatedCompanies = unallocatedCompaniesInfo.unallocatedCompanies;
    this.model.rounds.isAllCompaniesAllocated = unallocatedCompaniesInfo.isAllCompaniesAllocated;
    this.showRoundsEmitter.emit(true);
  }


  goToGroups(): void {
    if (this.model.rounds.areInEditMode) {
      this.notificationsService.warning(NOTIFICATIONS.SAVE_ROUNDS_BEFORE_LEAVING_WARNING);
    } else {
      this.resetShowRoundsTableData();
    }
  }

  /**
   * After user go back from ROUNDS -> GROUPS
   * - clear saved value, which sub group should be shown
   * - show main table
   * - show charts (emit)
   *
   * Should be called if user switch model - (bug fix)
   */
  resetShowRoundsTableData(): void {
    this.model.rounds.show.clear();
    this.model.showMainTable = true;
    this.showRoundsEmitter.emit(false);
    this.fsScrollPositionService.restoreScrollPosition();
  }


  toggleSubGroupVisibility(grIndex: number): void {
    this.model.subGroup.visibility[grIndex].visible = !this.model.subGroup.visibility[grIndex].visible;
  }

  checkSubGroupVisibility(grIndex: number): void {
    const fundStrategyArray = this.form.controls.fundStrategy as FormArray;
    this.model.subGroup.visibility = fundStrategyArray.controls.map(() => ({ visible: true }));
    this.model.subGroup.visibility[grIndex].visible = true;
  }

  /**
   * Change the position of the round in the round list.
   *
   * `initialCompaniesNumber` -> total number of the companies, should not be changed
   * We change period position in array of roundsData and set new roundOrders for all periods
   *
   * @param event drop object, contains information about previous item index, current item index etc.
   * @param subGroup selected subGroup data
   * @param grIndex index of selected group
   * @param sbIndex index of selected subgroup
   */
  cdkDropRound(event: CdkDragDrop<string[]>, subGroup: SubGroupFormGroup, grIndex: number, sbIndex: number): void {
    if (event.previousIndex === event.currentIndex) {
      return;
    }
    const initialCompaniesNumber = subGroup.value.roundsData[0].companiesNumber;
    moveItemInArray(subGroup.controls.roundsData.controls, event.previousIndex, event.currentIndex);
    subGroup.controls.roundsData.controls.forEach((round, index) => {
      round.patchValue({ roundOrder: index });
    });

    // the initial number of companies, that should be saved
    subGroup.controls.roundsData.at(0).patchValue({ companiesNumber: initialCompaniesNumber });

    this.updateSubGroup(subGroup, grIndex, sbIndex);
  }

  cdkDropSubGroup(event: CdkDragDrop<string[]>, subGroups: FormArray<SubGroupFormGroup>, grIndex: number): Promise<void> {
    if (event.previousIndex === event.currentIndex) {
      return;
    }
    this.updateModelHandler(EditStatus.updateSubGroupOrder, grIndex, event.previousIndex, event.currentIndex);

    moveItemInArray(subGroups.controls, event.previousIndex, event.currentIndex);
    subGroups.controls.forEach((sb, index) => {
      sb.patchValue({ subGrOrder: index });
    });
  }

  activateSaveUnallocatedCompaniesTooltip(id: string): void {
    if (this.model.rounds.isAllCompaniesAllocated) {
      return;
    }
    this.toolTipSettings = {
      show: true,
      coordinates: document?.getElementById(id).getBoundingClientRect(),
      title: 'Please allocate all companies',
      xShift: '-8em',
      positionType: 'left',
    };
  }

  activateTooltip(grIndex: number, sbIndex: number, rIndex: number, id: string): void {
    if (!this.model.rounds.unallocatedCompanies.get(grIndex + '-' + sbIndex + '-' + rIndex)) {
      return;
    }
    this.toolTipSettings = {
      show: true,
      coordinates: document?.getElementById(id + rIndex).getBoundingClientRect(),
      title: 'Unallocated companies',
      xShift: '0em',
      positionType: 'centered',
    };
  }


  showSubGroupTooltip(subGroup: SubGroupFormGroup, id: string): void {
    this.subGroupTooltipSettings = {
      show: true,
      data: FsChartsCalcService.getSubGroupTooltipAnalyticsData(subGroup),
      coordinates: document?.getElementById(id)?.getBoundingClientRect(),
    };
  }

  closeTooltip(): void {
    this.toolTipSettings.show = false;
  }

  moveGroup(grIndex: number, direction: 'up' | 'down'): void {
    this.updateModelHandler(EditStatus.updateGroupOrder, grIndex, null, direction === 'up' ? grIndex - 1 : grIndex + 1);
  }

  /** If model in creating mode (not saved), user should see the SUGGESTED data
   * All buttons should be disabled.
   */
  private prepareModelBlocked(): void {
    if (this.model.rounds.areInEditMode) {
      this.model.showMainTable = true;
    }
    this.groups = SUGGESTED_GROUPS;
    this.model.showMainTable = true;
    this.prepareFormAndVisibility();
  }

  /** Initialize the model form, calculate totals and make all subgroups visible.
   */
  private prepareFormAndVisibility(): void {
    this.form = this.formCreationService.fillFormFromFs(this.groups);
    this.model.subGroup.visibility = this.groups.map(() => ({ visible: true }));
    this.recalculateTotals();
  }

  /** Sends all model updates to the parent class.
   * Model could be blocked, if it is in creating mode. The model
   * should be saved first. All other actions in this case are forbidden. Model is blocked -> return.
   *
   * @param message: add a new group; update group name; duplicate group, etc.
   * @param grIndex is the group index.
   * @param sbIndex is the sub-group index.
   */
  private updateModelHandler(message?: EditStatus, grIndex?: number, sbIndex?: number, newOrder?: number): void {
    const fundStrategyValue = this.form.controls.fundStrategy.getRawValue() as GroupFS[];

    const saveModelEmitterDTO: SaveModelEmitterDTO = {
      groups: fundStrategyValue,
      message,
      grIndex,
      sbIndex,
      newOrder,
    };
    this.updateModelEmitter.emit(saveModelEmitterDTO);
  }

  /**
   * Calculates groups 'total' and 'grandTotal'.
   */
  private recalculateTotals(): void {
    const fundStrategyArray = this.form.controls.fundStrategy as FormArray;
    this.model.totals.subTotal = [];
    fundStrategyArray.controls.forEach((group, index) => {
      this.model.totals.subTotal[index] = this.fundStrategyService.calculateSubTotals(index, fundStrategyArray);
    });
    this.model.totals.grand = this.fundStrategyService.calculateGrandTotal(this.model.totals.subTotal);
  }

  private prepareDataForExportExcel(): void {
    const gr = this.form.controls.fundStrategy.controls;
    const data = {
      groups: gr,
      metrics: this.metricsData,
    };
    this.exportExcelEmitter.emit(data);
  }
}

